import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Upload, X, AlertCircle, CheckCircle, Download, Loader, FileText } from 'lucide-react';

const csvEscape = (value) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const getProgrammeName = (programme) => (
  programme?.name
  || programme?.programmeName
  || programme?.programName
  || programme?.title
  || ''
).trim();

const isCsvFile = (file) => /\.csv$/i.test(file?.name || '');

const formatFileSize = (bytes = 0) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function BulkUploadModal({ isOpen, onClose, institutionId, programmes = [], type = 'courses', apiBase = '/api' }) {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);

  const typeLabel = type === 'courses' ? 'Courses' : 'Programmes';
  const endpoint = type === 'courses' ? `${apiBase}/catalog/courses/bulk-import` : `${apiBase}/catalog/programmes/bulk-import`;
  const sampleFileName = type === 'courses' ? 'sample-courses.csv' : 'sample-programmes.csv';
  const sampleHeaders = type === 'courses'
    ? ['programmeName', 'name', 'fee', 'seats', 'minimumQualification', 'lateralEntryAccepted', 'active']
    : ['name', 'description', 'active'];
  const availableProgrammeNames = programmes.map(getProgrammeName).filter(Boolean);
  const availableProgrammeRows = availableProgrammeNames.length
    ? availableProgrammeNames.map((programmeName, index) => [
        programmeName,
        index === 0 ? 'Computer Science Engineering' : '',
        index === 0 ? '25000' : '',
        index === 0 ? '60' : '',
        index === 0 ? 'Higher Secondary' : '',
        index === 0 ? 'false' : '',
        index === 0 ? 'true' : '',
      ])
    : [['No programmes found for this institution', 'Computer Science Engineering', '25000', '60', 'Higher Secondary', 'false', 'true']];
  const sampleRows = type === 'courses'
    ? availableProgrammeRows
    : [['B.Tech', 'Bachelor of Technology programme', 'true']];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
	      const csvFile = Array.from(droppedFiles).find(isCsvFile);
	      if (csvFile) {
	        setFile(csvFile);
	        setUploadResults(null);
	      } else {
        toast.error('Please drop a CSV file');
      }
    }
  };

	  const handleFileSelect = (e) => {
	    const selectedFile = e.target.files?.[0];
	      if (selectedFile && isCsvFile(selectedFile)) {
	        setFile(selectedFile);
	        setUploadResults(null);
	    } else {
	      toast.error('Please select a CSV file');
	    }
	  };

	  const handleUpload = async () => {
	    if (!file) {
	      toast.error('Please select a file');
	      return;
	    }
	    if (!institutionId) {
	      toast.error('Institution is still loading. Please try again.');
	      return;
	    }

    const formData = new FormData();
    formData.append('file', file);
    if (institutionId) formData.append('institutionId', institutionId);

    setUploading(true);
    try {
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

	      if (response.status === 200) {
	        const failedRows = response.data.failedRows || [];
	        const createdRows = response.data.created || response.data.results || [];
	        const results = {
	          ...response.data,
	          success: failedRows.length === 0,
	          results: createdRows,
	          errors: failedRows.map(row => `Row ${row.rowNumber}: ${row.reason}`),
	          message: response.data.message || `${typeLabel} uploaded successfully`,
	        };
	        setUploadResults(results);
	        if (failedRows.length) {
	          toast.error(`${failedRows.length} row(s) failed`);
	        } else {
	          toast.success(`${typeLabel} uploaded successfully!`);
	        }
	      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
      setUploadResults({
        success: false,
        message: errorMessage,
        errors: error.response?.data?.errors || [],
      });
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csv = [
      sampleHeaders.map(csvEscape).join(','),
      ...sampleRows.map(row => row.map(csvEscape).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = sampleFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

	  const handleClose = () => {
	    setFile(null);
	    setUploadResults(null);
	    if (fileInputRef.current) fileInputRef.current.value = '';
	    onClose();
	  };

	  const openFilePicker = () => {
	    if (fileInputRef.current) fileInputRef.current.value = '';
	    fileInputRef.current?.click();
	  };

	  const clearSelectedFile = () => {
	    setFile(null);
	    if (fileInputRef.current) fileInputRef.current.value = '';
	  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">Bulk Upload {typeLabel}</h2>
            <p className="text-sm text-slate-500">Upload multiple {typeLabel.toLowerCase()} via CSV file</p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!uploadResults ? (
            <div className="space-y-4">
              {/* File Upload Area */}
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
	                onClick={openFilePicker}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-300 hover:border-slate-400 bg-slate-50'
                }`}
              >
                <Upload size={32} className="mx-auto mb-3 text-slate-400" />
                <p className="text-sm font-bold text-slate-700 mb-1">
                  {file ? file.name : 'Drag and drop your CSV file here'}
                </p>
                <p className="text-xs text-slate-500">or click to select</p>
                <input
	                  ref={fileInputRef}
	                  type="file"
	                  accept=".csv"
	                  onChange={handleFileSelect}
	                  onClick={(e) => e.stopPropagation()}
	                  className="hidden"
	                />
	              </div>

	              {file && (
	                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
	                  <div className="flex items-center gap-3 min-w-0">
	                    <div className="w-10 h-10 rounded-lg bg-white border border-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
	                      <FileText size={18} />
	                    </div>
	                    <div className="min-w-0">
	                      <p className="text-sm font-bold text-slate-800 truncate">{file.name}</p>
	                      <p className="text-xs text-slate-500">{formatFileSize(file.size)} selected</p>
	                    </div>
	                  </div>
	                  <button
	                    type="button"
	                    onClick={clearSelectedFile}
	                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-white"
	                    aria-label="Remove selected file"
	                  >
	                    <X size={16} />
	                  </button>
	                </div>
	              )}

	              {/* Sample CSV Download */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-bold text-indigo-900">Need help formatting your CSV?</h4>
	                    <p className="text-xs text-indigo-700 mt-1">
	                      {type === 'courses'
	                        ? 'Download a sample CSV with every available programme name for this institution.'
	                        : 'Download a sample CSV to see the required columns and format.'}
	                    </p>
                  </div>
                  <button
                    onClick={downloadSampleCSV}
                    className="shrink-0 inline-flex items-center gap-2 px-3 py-2 bg-white text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
                  >
                    <Download size={14} />
                    Download Sample
                  </button>
                </div>
              </div>

              {/* CSV Format Guide */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-slate-900 mb-3">Required Columns</h4>
                <div className="text-xs text-slate-600 space-y-2">
                  {type === 'courses' ? (
                    <>
	                      <p>• <span className="font-semibold">programmeName</span> - Must exactly match one of this institution's programmes</p>
	                      <p>• <span className="font-semibold">name</span> - Course name</p>
	                      <p>• <span className="font-semibold">fee</span> - Course fee (decimal)</p>
	                      <p>• <span className="font-semibold">seats</span> - Number of seats (integer)</p>
	                      <p>Optional: type, totalSeats, seatsAvailable, minimumQualification, lateralEntryAccepted, active</p>
	                      <p>Category is selected automatically from the institution.</p>
	                    </>
	                  ) : (
	                    <>
	                      <p>• <span className="font-semibold">name</span> - Programme name</p>
	                      <p>Optional: description, active</p>
	                      <p>Category is selected automatically from the institution.</p>
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex-1 px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {uploading && <Loader size={16} className="animate-spin" />}
                  Upload {uploading ? 'ing...' : typeLabel}
                </button>
              </div>
            </div>
          ) : (
            /* Upload Results */
            <div className="space-y-4">
              {uploadResults.success ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
                  <CheckCircle size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-emerald-900">Upload Successful!</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      {uploadResults.message || `${uploadResults.results?.length || 0} ${typeLabel.toLowerCase()} imported successfully.`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                  <AlertCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-900">Upload Failed</p>
                    <p className="text-xs text-red-700 mt-1">{uploadResults.message || 'An error occurred during upload'}</p>
                  </div>
                </div>
              )}

              {/* Error Details */}
              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-slate-900 mb-3">Errors</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {uploadResults.errors.map((error, idx) => (
                      <div key={idx} className="text-xs text-slate-600 bg-white p-2 rounded border border-slate-100">
                        <p className="font-semibold text-slate-700">{error.message || error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Result Summary */}
              {uploadResults.results && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h4 className="text-sm font-bold text-slate-900 mb-3">Results Summary</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {uploadResults.results.map((result, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-slate-100">
                        <p className="font-semibold text-slate-700">{result.name || result.title || result.id}</p>
                        <p className="text-slate-500">Status: {result.status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
	                  onClick={() => {
	                    setFile(null);
	                    setUploadResults(null);
	                    if (fileInputRef.current) fileInputRef.current.value = '';
	                  }}
                  className="flex-1 px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Upload Another File
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
